import { getAllOrderReports } from '@/features/report/services/reportClient'
import { logger } from '@/sentry.config'
import type { OrderReportResponse, OrderReportResultsPayload } from '@/types/OrderReportTypes'
import type { OrderFormInfoData } from '@/types/RequisitionFormTypes'
import { sanitizeLimitedInlineHtml } from '@/utils/sanitizeLimitedInlineHtml'
import { BookOpen, ChevronDown, InfoCircle } from 'flowbite-react-icons/outline'
import React, { useEffect, useMemo, useRef, useState } from 'react'

interface TestResultsCardProps {
  orderId?: string
  orderData?: OrderFormInfoData
  onReportChange?: (reportId: string) => void
  /** When true, use parent-provided list instead of getAllOrderReports (dedupe with OrderResultReady). */
  useParentReportList?: boolean
  parentReports?: OrderReportResponse[]
  parentReportsLoading?: boolean
  parentReportsError?: string | null
}

type SortKey = {
  generatedAtMs: number | null
  version: number | null
}

const parseSortKey = (report: OrderReportResponse): SortKey => {
  const generatedAtMs = report.generated_at ? new Date(report.generated_at).getTime() : NaN
  const safeGeneratedAtMs = Number.isFinite(generatedAtMs) && generatedAtMs > 0 ? generatedAtMs : null
  const version = report.version ?? null
  return { generatedAtMs: safeGeneratedAtMs, version }
}

const GUIDANCE_KEYS = ['summary', 'narrative', 'text', 'message', 'recommendation'] as const

function guidanceFromBlob(guidance: Record<string, unknown> | null | undefined): string {
  if (!guidance || typeof guidance !== 'object') return ''
  for (const key of GUIDANCE_KEYS) {
    const v = guidance[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

/**
 * Renders guidance text that may contain inline emphasis markup (e.g.
 * `<i>IRF8</i>` in fact_text). The string is sanitized down to a small tag
 * allowlist (i/em/b/strong/span/br/p) before rendering; `dangerouslySetInnerHTML`
 * is required to interpret the markup and is safe post-sanitization.
 */
function InlineHtml({ html, className }: { html: string; className?: string }) {
  return <span className={className} dangerouslySetInnerHTML={{ __html: sanitizeLimitedInlineHtml(html) }} />
}

const GUIDANCE_DISCLAIMER_SHORT =
  'Clinical guidance is informational and is not a substitute for professional medical judgment. ' +
  'The laboratory report may contain additional non-diagnostic variants not summarized here.'
const GUIDANCE_DISCLAIMER_FULL =
  'The Inflection Medicine platform is an information resource only and is not intended or implied as a ' +
  'substitute for professional medical advice or to be relied upon to provide diagnoses, treatment or ' +
  'otherwise supersede or replace the clinical judgment of licensed medical professionals acting within their ' +
  'scope of practice. The platform is not intended to replace patient evaluation and monitoring by licensed ' +
  'medical professionals, and practitioners should independently review and confirm any information or ' +
  'recommendations presented in the platform based on their own medical judgment.'

/**
 * CGE legal disclaimer shown under each guidance block's approval date. The
 * short line is always visible; the info icon surfaces the full disclaimer via
 * a tooltip on hover, click, or keyboard focus (keyboard-focusable button with
 * aria-label + role="tooltip" association).
 */
function GuidanceDisclaimer({ idSuffix }: { idSuffix: string }) {
  const [open, setOpen] = useState(false)
  const tooltipId = `guidance-disclaimer-${idSuffix}`
  return (
    <div className="flex items-start gap-1.5 text-sm font-medium leading-5 text-text-body dark:text-gray-400">
      <span>{GUIDANCE_DISCLAIMER_SHORT}</span>
      <span className="relative inline-flex shrink-0">
        <button
          type="button"
          aria-label="Full clinical guidance disclaimer"
          aria-describedby={tooltipId}
          className="peer inline-flex h-4 w-4 items-center justify-center rounded-full text-text-body dark:text-gray-400 transition-colors hover:text-text-heading dark:hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
          onClick={() => setOpen((v) => !v)}
          onBlur={() => setOpen(false)}
        >
          <InfoCircle className="h-4 w-4" aria-hidden />
        </button>
        <span
          id={tooltipId}
          role="tooltip"
          className={`absolute bottom-full right-0 z-30 mb-2 max-w-[calc(100vw-2rem)] w-72 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-xs font-normal leading-5 text-text-body dark:text-gray-400 shadow-lg sm:w-96 ${
            open ? 'block' : 'hidden'
          } peer-hover:block peer-focus-visible:block`}
        >
          {GUIDANCE_DISCLAIMER_FULL}
        </span>
      </span>
    </div>
  )
}

function stringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    .map((s) => s.trim())
}

type SymptomItem = { label: string; value: string }

type ParsedCitation = { pmid?: string; title?: string }

type ParsedGuidanceFact = {
  id: string
  factType: string
  text: string
  citations: ParsedCitation[]
  /** Curator metadata (e.g. test_type for Confirmatory Test) — drives Vision 573:12234 LABS vs IMAGING. */
  metadata?: Record<string, unknown>
}

/** Facts bucketed for Signs / Symptoms / Lab / Tests columns (Figma grouping). */
type SignsSymptomsLabGroups = {
  signs: ParsedGuidanceFact[]
  symptoms: ParsedGuidanceFact[]
  labAbnormalities: ParsedGuidanceFact[]
  tests: ParsedGuidanceFact[]
  other: ParsedGuidanceFact[]
}

type ParsedCgeGuidanceBlock = {
  key: string
  conditionTitle: string
  conditionSubtitle: string
  description: string
  approvedAt: string | null
  signsSymptomsLabGroups: SignsSymptomsLabGroups
  clinicalConsiderations: ParsedGuidanceFact[]
  resources: ParsedGuidanceFact[]
  warnings: ParsedGuidanceFact[]
}

const FACT_TYPES_SIGNS_SYMPTOMS_LAB = new Set([
  'Symptom',
  'Key Symptom',
  'Key Sign',
  'Key Phenotype',
  'Key Lab Abnormality',
  'Clinically Indicated Test',
  'Confirmatory Test',
])

const FACT_TYPES_CLINICAL = new Set(['Consultation', 'Intervention', 'Prognosis'])

const FACT_TYPES_RESOURCE = new Set(['Resource'])

/** Guidance facts whose `fact_type` is "Warning" surface in the alert banner, not Clinical Considerations. */
function isWarningFactType(factType: string): boolean {
  return factType.trim().toLowerCase() === 'warning'
}

function emptySignsSymptomsLabGroups(): SignsSymptomsLabGroups {
  return { signs: [], symptoms: [], labAbnormalities: [], tests: [], other: [] }
}

function countSignsSymptomsLabGroups(g: SignsSymptomsLabGroups): number {
  return (
    g.signs.length + g.symptoms.length + g.labAbnormalities.length + g.tests.length + g.other.length
  )
}

function appendSignsSymptomsLabFact(
  groups: SignsSymptomsLabGroups,
  factType: string,
  row: ParsedGuidanceFact,
) {
  switch (factType) {
    case 'Key Sign':
    case 'Key Phenotype':
      groups.signs.push(row)
      break
    case 'Symptom':
    case 'Key Symptom':
      groups.symptoms.push(row)
      break
    case 'Key Lab Abnormality':
      groups.labAbnormalities.push(row)
      break
    case 'Clinically Indicated Test':
    case 'Confirmatory Test':
      groups.tests.push(row)
      break
    default:
      groups.other.push(row)
  }
}

const SIGNS_SYMPTOMS_LAB_COLUMNS: { groupKey: keyof SignsSymptomsLabGroups; heading: string }[] = [
  { groupKey: 'signs', heading: 'SIGNS' },
  { groupKey: 'symptoms', heading: 'SYMPTOMS' },
  { groupKey: 'labAbnormalities', heading: 'LAB ABNORMALITIES' },
  { groupKey: 'other', heading: 'OTHER' },
]

function GroupedSignsSymptomsLabSection({ groups }: { groups: SignsSymptomsLabGroups }) {
  const visible = SIGNS_SYMPTOMS_LAB_COLUMNS.filter((c) => groups[c.groupKey].length > 0)
  if (visible.length === 0) return null

  return (
    <div className="flex flex-col gap-2 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6">
      <div className="flex flex-col gap-1">
        <h4 className="text-lg font-semibold tracking-tight text-text-heading dark:text-white">
          Key Signs, Symptoms & Lab Abnormalities
        </h4>
        <p className="text-sm font-medium leading-5 text-text-body dark:text-gray-400">
          Clinical features shown may be observed in patients with this condition.
        </p>
      </div>

      <div className="flex w-full flex-col gap-4 md:flex-row md:items-stretch">
        {visible.map((col, colIndex) => {
          const facts = groups[col.groupKey]
          return (
            <div
              key={col.groupKey}
              className={`flex min-h-0 min-w-0 flex-1 flex-col p-4 ${
                colIndex > 0 ? 'md:border-l md:border-border-base dark:md:border-gray-700' : ''
              }`}
            >
              <p className="text-xs font-bold uppercase leading-5 text-text-body dark:text-gray-400">{col.heading}</p>
              <div className="h-2 shrink-0" aria-hidden />
              <div className="flex flex-col">
                {facts.map((fact, rowIndex) => (
                  <div
                    key={fact.id}
                    className={
                      rowIndex < facts.length - 1
                        ? 'border-b border-border-light dark:border-gray-700 py-3'
                        : 'py-3'
                    }
                  >
                    <p className="text-base font-medium leading-6 text-text-body dark:text-gray-400">
                      <InlineHtml html={fact.text} />
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function pickRecordString(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

function parseCgeGuidanceItem(item: Record<string, unknown>, index: number): ParsedCgeGuidanceBlock | null {
  const phenotype =
    item['phenotype'] && typeof item['phenotype'] === 'object'
      ? (item['phenotype'] as Record<string, unknown>)
      : null
  const conditionTitle = phenotype ? pickRecordString(phenotype, ['name']) : ''
  const description = phenotype ? pickRecordString(phenotype, ['summary']) : ''

  const signsSymptomsLabGroups = emptySignsSymptomsLabGroups()
  const clinicalConsiderations: ParsedGuidanceFact[] = []
  const resources: ParsedGuidanceFact[] = []
  const warnings: ParsedGuidanceFact[] = []

  let syntheticFactId = 0
  const factsRaw = item['facts']
  if (Array.isArray(factsRaw)) {
    for (const f of factsRaw) {
      if (!f || typeof f !== 'object') continue
      const fo = f as Record<string, unknown>
      if (fo['hidden'] === true) continue
      const text = pickRecordString(fo, ['fact_text', 'factText'])
      if (!text) continue
      const factType = pickRecordString(fo, ['fact_type', 'factType']) || 'Fact'
      const id = pickRecordString(fo, ['id']) || `fact-${syntheticFactId++}`
      const citations: ParsedCitation[] = []
      if (Array.isArray(fo['citations'])) {
        for (const c of fo['citations']) {
          if (!c || typeof c !== 'object') continue
          const co = c as Record<string, unknown>
          const pmid = pickRecordString(co, ['pmid'])
          const title = pickRecordString(co, ['title'])
          if (pmid || title) citations.push({ pmid: pmid || undefined, title: title || undefined })
        }
      }
      const metaRaw = fo['metadata']
      const metadata =
        metaRaw && typeof metaRaw === 'object' && !Array.isArray(metaRaw)
          ? (metaRaw as Record<string, unknown>)
          : undefined
      const row: ParsedGuidanceFact = { id, factType, text, citations, metadata }
      if (isWarningFactType(factType)) warnings.push(row)
      else if (FACT_TYPES_RESOURCE.has(factType)) resources.push(row)
      else if (FACT_TYPES_CLINICAL.has(factType)) clinicalConsiderations.push(row)
      else if (FACT_TYPES_SIGNS_SYMPTOMS_LAB.has(factType)) appendSignsSymptomsLabFact(signsSymptomsLabGroups, factType, row)
      else clinicalConsiderations.push(row)
    }
  }

  const approvedAt =
    pickRecordString(item, ['approved_at', 'approvedAt']) || null

  const hasBody =
    Boolean(conditionTitle) ||
    Boolean(description) ||
    countSignsSymptomsLabGroups(signsSymptomsLabGroups) > 0 ||
    clinicalConsiderations.length > 0 ||
    resources.length > 0 ||
    warnings.length > 0

  if (!hasBody) return null

  const key = pickRecordString(item, ['id']) || `guidance-${index}`

  return {
    key,
    conditionTitle,
    conditionSubtitle: '',
    description,
    approvedAt,
    signsSymptomsLabGroups,
    clinicalConsiderations,
    resources,
    warnings,
  }
}

function CitationLinks({ citations }: { citations: ParsedCitation[] }) {
  if (!citations.length) return null
  return (
    <ul className="mt-1 list-none space-y-0.5 pl-0 text-xs text-text-body dark:text-gray-400">
      {citations.map((c, i) => {
        const pmid = c.pmid?.trim()
        const href = pmid ? `https://pubmed.ncbi.nlm.nih.gov/${encodeURIComponent(pmid)}/` : undefined
        const label = c.title?.trim() || (pmid ? `PMID ${pmid}` : 'Reference')
        return (
          <li key={`${pmid ?? 'ref'}-${i}`}>
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-brand-600 dark:hover:text-brand-400"
              >
                {label}
              </a>
            ) : (
              label
            )}
          </li>
        )
      })}
    </ul>
  )
}

type ClinicalConsiderationsVisionBuckets = {
  consultations: ParsedGuidanceFact[]
  evalLabs: ParsedGuidanceFact[]
  evalImaging: ParsedGuidanceFact[]
  evalPrognosis: ParsedGuidanceFact[]
  evalOther: ParsedGuidanceFact[]
  treatMedications: ParsedGuidanceFact[]
  treatDietary: ParsedGuidanceFact[]
  treatProcedures: ParsedGuidanceFact[]
  treatOther: ParsedGuidanceFact[]
  emergingTherapies: ParsedGuidanceFact[]
  clinicalTrials: ParsedGuidanceFact[]
}

function metadataString(meta: Record<string, unknown> | undefined, key: string): string {
  if (!meta) return ''
  const v = meta[key]
  return typeof v === 'string' ? v.trim().toLowerCase() : ''
}

/** Clinically Indicated / Confirmatory tests → LABS vs IMAGING (metadata.test_type + text). */
function partitionEvaluationTestFacts(tests: ParsedGuidanceFact[]): {
  labs: ParsedGuidanceFact[]
  imaging: ParsedGuidanceFact[]
} {
  const labs: ParsedGuidanceFact[] = []
  const imaging: ParsedGuidanceFact[] = []
  for (const f of tests) {
    const tt = metadataString(f.metadata, 'test_type')
    const textLow = f.text.toLowerCase()
    if (
      tt === 'imaging' ||
      /\b(mri|mrs|echo|ultrasound|ct scan|radiograph|x-ray|tomograph)\b/.test(textLow)
    ) {
      imaging.push(f)
    } else {
      labs.push(f)
    }
  }
  return { labs, imaging }
}

/**
 * Map guidance facts to Figma 573:12234 Clinical Considerations.
 * - `evaluationTestFacts`: Clinically Indicated / Confirmatory rows (same JSON, also in phenotype `tests` bucket).
 */
function partitionClinicalConsiderationsForVision(
  clinicalFacts: ParsedGuidanceFact[],
  evaluationTestFacts: ParsedGuidanceFact[],
): ClinicalConsiderationsVisionBuckets {
  const out: ClinicalConsiderationsVisionBuckets = {
    consultations: [],
    evalLabs: [],
    evalImaging: [],
    evalPrognosis: [],
    evalOther: [],
    treatMedications: [],
    treatDietary: [],
    treatProcedures: [],
    treatOther: [],
    emergingTherapies: [],
    clinicalTrials: [],
  }

  const testSplit = partitionEvaluationTestFacts(evaluationTestFacts)
  out.evalLabs.push(...testSplit.labs)
  out.evalImaging.push(...testSplit.imaging)

  for (const f of clinicalFacts) {
    const t = f.factType.trim().toLowerCase()
    const textLow = f.text.toLowerCase()

    if (t === 'consultation' || t.startsWith('consultation')) {
      out.consultations.push(f)
      continue
    }

    if (t === 'prognosis' || t.startsWith('prognosis')) {
      out.evalPrognosis.push(f)
      continue
    }

    if (t.includes('emerging') || t.includes('exploratory')) {
      out.emergingTherapies.push(f)
      continue
    }

    if (t.includes('clinical trial') || t.includes('clinical_trial') || t === 'trial') {
      out.clinicalTrials.push(f)
      continue
    }

    const isTreatmentFact =
      t === 'intervention' ||
      t.endsWith(' intervention') ||
      t === 'treatment' ||
      t === 'therapeutic' ||
      t === 'therapy'

    if (isTreatmentFact) {
      if (t.includes('medication') || t.includes('drug') || t.includes('pharma')) {
        out.treatMedications.push(f)
      } else if (t.includes('diet') || t.includes('dietary') || t.includes('nutrition')) {
        out.treatDietary.push(f)
      } else if (t.includes('procedure') || t.includes('surgery') || t.includes('transplant')) {
        out.treatProcedures.push(f)
      } else if (
        /\b(losartan|metformin|insulin|aspirin|rifampin|enzyme|inhibitor|therapy with|oral)\b/i.test(f.text) ||
        textLow.includes('supplement')
      ) {
        out.treatMedications.push(f)
      } else if (
        textLow.includes('diet') ||
        textLow.includes('ketogenic') ||
        textLow.includes('formula') ||
        textLow.includes('nutrition')
      ) {
        out.treatDietary.push(f)
      } else if (
        textLow.includes('transplant') ||
        textLow.includes('surgery') ||
        textLow.includes('procedure') ||
        textLow.includes('hsct')
      ) {
        out.treatProcedures.push(f)
      } else if (
        textLow.includes('gene therapy') ||
        textLow.includes('clinical trial') ||
        /\bnct\s*\d+/i.test(f.text) ||
        textLow.includes('[nct')
      ) {
        out.treatProcedures.push(f)
      } else {
        out.treatOther.push(f)
      }
      continue
    }

    if (
      t.includes('lab') ||
      t.includes('laboratory') ||
      t.includes('blood work') ||
      /\b(cbc|panel|serum|urine|metabolite|acidemia|acidosis)\b/.test(textLow)
    ) {
      out.evalLabs.push(f)
    } else if (
      t.includes('imaging') ||
      t.includes('radiology') ||
      t.includes('echo') ||
      t.includes('ultrasound') ||
      t.includes('mri') ||
      /\b(ct scan|echocardiogram|ultrasound|radiograph|x-ray)\b/.test(textLow)
    ) {
      out.evalImaging.push(f)
    } else {
      out.evalOther.push(f)
    }
  }
  return out
}

function primaryPubMedHref(citations: ParsedCitation[]): string | undefined {
  for (const c of citations) {
    const pmid = c.pmid?.trim()
    if (pmid) return `https://pubmed.ncbi.nlm.nih.gov/${encodeURIComponent(pmid)}/`
  }
  return undefined
}

const REFERENCES_CHIP_CLASS =
  'inline-flex shrink-0 items-center gap-1 rounded-full bg-background-tertiary dark:bg-gray-800 px-2 py-1 text-xs font-medium text-text-body dark:text-gray-400 no-underline transition-colors hover:bg-background-quaternary dark:hover:bg-gray-700'

/** Clinical Considerations: chip opens first PubMed URL; no separate citation line. */
function ReferencesChipLink({ citations }: { citations: ParsedCitation[] }) {
  const href = primaryPubMedHref(citations)
  const first = citations[0]
  const ariaLabel =
    citations.length > 1
      ? `Open first of ${citations.length} PubMed references in a new tab`
      : first?.title?.trim() || (first?.pmid?.trim() ? `PubMed PMID ${first.pmid.trim()}` : 'References')

  if (!href) {
    return (
      <span className={REFERENCES_CHIP_CLASS}>
        <BookOpen className="h-3.5 w-3.5 shrink-0" aria-hidden />
        References
      </span>
    )
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className={REFERENCES_CHIP_CLASS}
      onClick={(e) => e.stopPropagation()}
    >
      <BookOpen className="h-3.5 w-3.5 shrink-0" aria-hidden />
      References
    </a>
  )
}

function VisionFactRow({
  fact,
  showBottomBorder,
}: {
  fact: ParsedGuidanceFact
  showBottomBorder: boolean
}) {
  const hasCitations = fact.citations.length > 0
  const href = primaryPubMedHref(fact.citations)
  const isClickable = Boolean(href)

  const openReference = () => {
    if (href) window.open(href, '_blank', 'noopener,noreferrer')
  }

  return (
    <div
      className={`flex flex-col gap-2 bg-white dark:bg-gray-900 px-4 py-4 ${
        isClickable
          ? 'cursor-pointer transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800'
          : ''
      } ${showBottomBorder ? 'border-b border-border-base dark:border-gray-700' : ''}`}
      {...(isClickable
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: openReference,
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openReference()
              }
            },
          }
        : {})}
    >
      <div className="flex w-full min-w-0 items-start gap-3">
        <p className="min-w-0 flex-1 text-base font-medium leading-5 text-text-heading dark:text-white">
          <InlineHtml html={fact.text} />
        </p>
        {hasCitations ? <ReferencesChipLink citations={fact.citations} /> : null}
      </div>
      {hasCitations ? (
        <div className="sr-only">
          <CitationLinks citations={fact.citations} />
        </div>
      ) : null}
    </div>
  )
}

function renderFactRows(facts: ParsedGuidanceFact[]) {
  return facts.map((fact, i) => (
    <VisionFactRow key={fact.id} fact={fact} showBottomBorder={i < facts.length - 1} />
  ))
}

function VisionFactSection({
  idPrefix,
  title,
  sectionKey,
  isOpen,
  onToggle,
  children,
}: {
  idPrefix: string
  title: string
  sectionKey: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  const btnId = `${idPrefix}-${sectionKey}-trigger`
  const panelId = `${idPrefix}-${sectionKey}-panel`
  return (
    <>
      <div className="flex w-full items-center pt-2">
        <button
          type="button"
          id={btnId}
          className="flex items-center gap-1.5 rounded-base text-left text-text-heading dark:text-white hover:bg-background-secondary-medium dark:hover:bg-gray-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <ChevronDown
            className={`h-4 w-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
            aria-hidden
          />
          <span className="text-base font-bold leading-5">{title}</span>
        </button>
      </div>
      {isOpen ? (
        <div id={panelId} role="region" aria-labelledby={btnId} className="w-full pl-6">
          {children}
        </div>
      ) : null}
    </>
  )
}

/** Figma 573:12234 — Clinical Considerations fact groups (nested sections + subgroup rows). */
function ClinicalConsiderationsFactGroups({
  idPrefix,
  facts,
  evaluationTestFacts,
}: {
  idPrefix: string
  facts: ParsedGuidanceFact[]
  evaluationTestFacts: ParsedGuidanceFact[]
}) {
  const buckets = useMemo(
    () => partitionClinicalConsiderationsForVision(facts, evaluationTestFacts),
    [facts, evaluationTestFacts],
  )
  const [open, setOpen] = useState({
    evaluations: true,
    treatments: true,
    emerging: true,
    trials: true,
    consultations: true,
  })

  const hasEval =
    buckets.evalLabs.length +
      buckets.evalImaging.length +
      buckets.evalPrognosis.length +
      buckets.evalOther.length >
    0
  const hasTreat =
    buckets.treatMedications.length +
      buckets.treatDietary.length +
      buckets.treatProcedures.length +
      buckets.treatOther.length >
    0
  const hasEmerging = buckets.emergingTherapies.length > 0
  const hasTrials = buckets.clinicalTrials.length > 0
  const hasCons = buckets.consultations.length > 0

  if (!hasEval && !hasTreat && !hasEmerging && !hasTrials && !hasCons) return null

  const toggle = (k: keyof typeof open) => () => setOpen((p) => ({ ...p, [k]: !p[k] }))

  return (
    <div className="flex flex-col gap-2 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6">
      <h4 className="text-lg font-semibold tracking-tight text-text-heading dark:text-white">
        Clinical Considerations
      </h4>

      {hasEval ? (
        <VisionFactSection
          idPrefix={idPrefix}
          sectionKey="evaluations"
          title="Evaluations"
          isOpen={open.evaluations}
          onToggle={toggle('evaluations')}
        >
          {buckets.evalLabs.length > 0 ? renderFactRows(buckets.evalLabs) : null}
          {buckets.evalImaging.length > 0 ? renderFactRows(buckets.evalImaging) : null}
          {buckets.evalPrognosis.length > 0 ? renderFactRows(buckets.evalPrognosis) : null}
          {buckets.evalOther.length > 0 ? renderFactRows(buckets.evalOther) : null}
        </VisionFactSection>
      ) : null}

      {hasTreat ? (
        <VisionFactSection
          idPrefix={idPrefix}
          sectionKey="treatments"
          title="Interventions"
          isOpen={open.treatments}
          onToggle={toggle('treatments')}
        >
          {buckets.treatMedications.length > 0 ? renderFactRows(buckets.treatMedications) : null}
          {buckets.treatDietary.length > 0 ? renderFactRows(buckets.treatDietary) : null}
          {buckets.treatProcedures.length > 0 ? renderFactRows(buckets.treatProcedures) : null}
          {buckets.treatOther.length > 0 ? renderFactRows(buckets.treatOther) : null}
        </VisionFactSection>
      ) : null}

      {hasEmerging ? (
        <VisionFactSection
          idPrefix={idPrefix}
          sectionKey="emerging"
          title="Emerging & Exploratory Therapies"
          isOpen={open.emerging}
          onToggle={toggle('emerging')}
        >
          {renderFactRows(buckets.emergingTherapies)}
        </VisionFactSection>
      ) : null}

      {hasTrials ? (
        <VisionFactSection
          idPrefix={idPrefix}
          sectionKey="trials"
          title="Clinical Trials"
          isOpen={open.trials}
          onToggle={toggle('trials')}
        >
          {renderFactRows(buckets.clinicalTrials)}
        </VisionFactSection>
      ) : null}

      {hasCons ? (
        <VisionFactSection
          idPrefix={idPrefix}
          sectionKey="consultations"
          title="Consults"
          isOpen={open.consultations}
          onToggle={toggle('consultations')}
        >
          {renderFactRows(buckets.consultations)}
        </VisionFactSection>
      ) : null}
    </div>
  )
}

type GuidanceAccordionPanelProps = {
  title: string
  panelId: string
  buttonId: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}

function GuidanceAccordionPanel({
  title,
  panelId,
  buttonId,
  isOpen,
  onToggle,
  children,
}: GuidanceAccordionPanelProps) {
  return (
    <div className="overflow-hidden rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xs">
      <button
        type="button"
        id={buttonId}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-background-secondary-medium dark:hover:bg-gray-800"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-text-heading dark:text-white transition-transform duration-200 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
          aria-hidden
        />
        <span className="text-lg font-semibold text-text-heading dark:text-white">{title}</span>
      </button>
      {isOpen ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          className="border-t border-border-base dark:border-gray-700 px-4 pb-4 pt-3"
        >
          {children}
        </div>
      ) : null}
    </div>
  )
}

function normalizeSymptomItems(raw: unknown): SymptomItem[] {
  if (!Array.isArray(raw)) return []
  const out: SymptomItem[] = []
  for (const item of raw) {
    if (typeof item === 'string' && item.trim()) {
      out.push({ label: '', value: item.trim() })
      continue
    }
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>
      const label =
        typeof o['label'] === 'string'
          ? o['label']
          : typeof o['name'] === 'string'
            ? o['name']
            : ''
      const value =
        typeof o['value'] === 'string'
          ? o['value']
          : typeof o['detail'] === 'string'
            ? o['detail']
            : typeof o['description'] === 'string'
              ? o['description']
              : ''
      if (label.trim() || value.trim()) {
        out.push({ label: label.trim(), value: value.trim() })
      }
    }
  }
  return out
}

function parseGuidanceVision(
  guidance: Record<string, unknown> | null | undefined,
  clinicalResults: OrderReportResultsPayload | null | undefined,
) {
  const g = guidance && typeof guidance === 'object' ? guidance : {}
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = g[k]
      if (typeof v === 'string' && v.trim()) return v.trim()
    }
    return ''
  }

  const cgeBlocks: ParsedCgeGuidanceBlock[] = []
  const guidancesRaw = g['guidances']
  if (Array.isArray(guidancesRaw)) {
    guidancesRaw.forEach((item, i) => {
      if (item && typeof item === 'object') {
        const block = parseCgeGuidanceItem(item as Record<string, unknown>, i)
        if (block) cgeBlocks.push(block)
      }
    })
  }

  const firstCondition =
    clinicalResults?.variants?.find((v) => typeof v.condition === 'string' && v.condition.trim())?.condition?.trim() ??
    ''
  const conditionTitle = pick('conditionTitle', 'condition_title', 'title') || firstCondition
  const conditionSubtitle = pick('conditionSubtitle', 'condition_subtitle', 'subtitle')
  let description = pick('description', 'condition_description', 'overview')
  if (!description) {
    description = guidanceFromBlob(g as Record<string, unknown>)
  }
  const symptomItems = normalizeSymptomItems(
    g['symptoms'] ?? g['keySignsAndSymptoms'] ?? g['key_signs_and_symptoms'],
  )
  const clinicalBullets = [
    ...stringArray(g['clinicalConsiderations']),
    ...stringArray(g['clinical_considerations']),
  ]
  const guidanceWarnings = stringArray(g['warnings'])
  const extractionWarnings = stringArray(clinicalResults?.extraction_warnings)
  // Facts with fact_type "Warning" inside each guidance block surface in the alert banner.
  const factWarnings = cgeBlocks.flatMap((b) => b.warnings.map((f) => f.text)).filter((t) => t.trim())
  const allWarnings = [...extractionWarnings, ...guidanceWarnings, ...factWarnings]

  const hasLegacyConditionSection =
    cgeBlocks.length === 0 &&
    (Boolean(conditionTitle) ||
      Boolean(conditionSubtitle) ||
      Boolean(description) ||
      symptomItems.length > 0 ||
      clinicalBullets.length > 0)

  const hasConditionSection = cgeBlocks.length > 0 || hasLegacyConditionSection

  return {
    conditionTitle,
    conditionSubtitle,
    description,
    symptomItems,
    clinicalBullets,
    allWarnings,
    hasConditionSection,
    hasLegacyConditionSection,
    cgeBlocks,
  }
}

export const TestResultsCard: React.FC<TestResultsCardProps> = ({
  orderId,
  orderData,
  onReportChange,
  useParentReportList = false,
  parentReports = [],
  parentReportsLoading = false,
  parentReportsError = null,
}) => {
  const [reports, setReports] = useState<OrderReportResponse[]>([])
  const [selectedReportId, setSelectedReportId] = useState<string>('')
  const [isLoadingReports, setIsLoadingReports] = useState(false)
  const [reportsError, setReportsError] = useState<string | null>(null)
  const displayReports = useParentReportList ? parentReports : reports
  const displayLoading = useParentReportList ? parentReportsLoading : isLoadingReports
  const displayError = useParentReportList ? parentReportsError : reportsError
  /** Collapsible guidance sections: undefined = expanded (default). */
  const [guidanceAccordionOpen, setGuidanceAccordionOpen] = useState<Record<string, boolean>>({})

  const guidanceAccordionIsOpen = (key: string) => guidanceAccordionOpen[key] !== false
  const toggleGuidanceAccordion = (key: string) => {
    setGuidanceAccordionOpen((prev) => {
      const current = prev[key] !== false
      return { ...prev, [key]: !current }
    })
  }

  const callerFirst = orderData?.orderingProvider?.name?.firstName ?? ''
  const callerLast = orderData?.orderingProvider?.name?.lastName ?? ''
  const caller = useMemo(() => {
    const combined = `${callerFirst} ${callerLast}`.trim()
    return combined || 'spa'
  }, [callerFirst, callerLast])

  const sortedReports = useMemo(() => {
    const next = [...displayReports]
    next.sort((a, b) => {
      const aKey = parseSortKey(a)
      const bKey = parseSortKey(b)

      if (aKey.generatedAtMs !== null || bKey.generatedAtMs !== null) {
        const aMs = aKey.generatedAtMs ?? -1
        const bMs = bKey.generatedAtMs ?? -1
        if (aMs !== bMs) return bMs - aMs
      }

      if (aKey.version !== null || bKey.version !== null) {
        const aV = aKey.version ?? -1
        const bV = bKey.version ?? -1
        if (aV !== bV) return bV - aV
      }

      return a.id.localeCompare(b.id)
    })
    return next
  }, [displayReports])

  const latestReportId = useMemo(() => {
    return sortedReports[0]?.id ?? ''
  }, [sortedReports])

  const currentReportsRequestRef = useRef<string | null>(null)
  const onReportChangeRef = useRef(onReportChange)
  useEffect(() => {
    onReportChangeRef.current = onReportChange
  }, [onReportChange])

  useEffect(() => {
    if (useParentReportList) return

    if (!orderId) {
      setReports([])
      setSelectedReportId('')
      setIsLoadingReports(false)
      setReportsError('Order ID is required to fetch reports.')
      currentReportsRequestRef.current = null
      return
    }

    const requestId = `${orderId}-${caller}`
    currentReportsRequestRef.current = requestId

    const fetchAllReports = async () => {
      try {
        setIsLoadingReports(true)
        setReportsError(null)

        const allReports = await getAllOrderReports(orderId, caller)
        if (currentReportsRequestRef.current !== requestId) return

        setReports(allReports)
      } catch (error) {
        if (currentReportsRequestRef.current !== requestId) return
        logger.error('Failed to fetch reports', {
          orderId,
          error: error instanceof Error ? error.message : String(error),
        })
        setReportsError(error instanceof Error ? error.message : 'Failed to fetch reports.')
      } finally {
        if (currentReportsRequestRef.current === requestId) {
          setIsLoadingReports(false)
        }
      }
    }

    fetchAllReports()

    return () => {
      currentReportsRequestRef.current = null
    }
  }, [orderId, caller, useParentReportList])

  useEffect(() => {
    if (!latestReportId) {
      setSelectedReportId('')
      return
    }
    setSelectedReportId(latestReportId)
  }, [latestReportId])

  useEffect(() => {
    if (!selectedReportId) return
    onReportChangeRef.current?.(selectedReportId)
  }, [selectedReportId])

  const hasReports = sortedReports.length > 0

  const selectedReport = useMemo(
    () => sortedReports.find((r) => r.id === selectedReportId),
    [sortedReports, selectedReportId],
  )
  const clinicalResults = selectedReport?.results ?? null
  const vision = useMemo(
    () => parseGuidanceVision(selectedReport?.guidance ?? undefined, clinicalResults),
    [selectedReport, clinicalResults],
  )

  const showGuidanceVisionContent = vision.allWarnings.length > 0 || vision.hasConditionSection

  return (
    <div className="flex flex-col gap-4">
      {displayLoading ? (
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-text-body dark:text-gray-400 shadow-sm">
          Loading Results…
        </div>
      ) : displayError ? (
        <div className="rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 p-6 text-sm text-text-fg-error-strong dark:text-error-100 shadow-sm">
          {displayError}
        </div>
      ) : !hasReports ? (
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-text-body dark:text-gray-400 shadow-sm">
          No reports available for this order.
        </div>
      ) : showGuidanceVisionContent ? (
        <div className="flex flex-col gap-4 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm">
          {vision.allWarnings.length > 0 ? (
            <div
              className="flex flex-col gap-2 rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 p-4 text-text-fg-error-strong dark:text-error-100"
              role="alert"
            >
              <p className="text-lg font-semibold tracking-tight">Warnings</p>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {vision.allWarnings.map((w) => (
                  <li key={w}>
                    <InlineHtml html={w} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {vision.hasConditionSection ? (
            <div className="flex flex-col gap-4 border-t border-border-base dark:border-gray-700 pt-4">
              {vision.cgeBlocks.map((block) => {
                const cgeKey = `cge-${block.key}`
                return (
                  <GuidanceAccordionPanel
                    key={block.key}
                    title={block.conditionTitle || 'Clinical guidance'}
                    panelId={`test-results-${cgeKey}-panel`}
                    buttonId={`test-results-${cgeKey}-trigger`}
                    isOpen={guidanceAccordionIsOpen(cgeKey)}
                    onToggle={() => toggleGuidanceAccordion(cgeKey)}
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-1">
                        {block.approvedAt ? (
                          <p className="text-sm text-text-body dark:text-gray-400">
                            Guidance approved{' '}
                            {(() => {
                              const d = new Date(block.approvedAt)
                              return Number.isFinite(d.getTime())
                                ? d.toLocaleString(undefined, {
                                    dateStyle: 'medium',
                                    timeStyle: 'short',
                                  })
                                : block.approvedAt
                            })()}
                          </p>
                        ) : null}
                      </div>

                      {block.description ? (
                        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6">
                          <p className="text-base leading-7 text-text-body dark:text-gray-400">
                            <InlineHtml html={block.description} />
                          </p>
                        </div>
                      ) : null}

                      <GroupedSignsSymptomsLabSection groups={block.signsSymptomsLabGroups} />

                      {block.clinicalConsiderations.length > 0 ||
                      block.signsSymptomsLabGroups.tests.length > 0 ? (
                        <ClinicalConsiderationsFactGroups
                          idPrefix={`test-results-${cgeKey}-cc`}
                          facts={block.clinicalConsiderations}
                          evaluationTestFacts={block.signsSymptomsLabGroups.tests}
                        />
                      ) : null}

                      {block.resources.length > 0 ? (
                        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6">
                          <h4 className="text-lg font-bold leading-5 text-text-heading dark:text-white">
                            Resources
                          </h4>
                          <ul className="mt-2 list-disc space-y-2 pl-5 text-base text-text-body dark:text-gray-400">
                            {block.resources.map((fact) => (
                              <li key={fact.id}>
                                <InlineHtml html={fact.text} />
                                <CitationLinks citations={fact.citations} />
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  </GuidanceAccordionPanel>
                )
              })}

              {vision.hasLegacyConditionSection ? (
                <GuidanceAccordionPanel
                  title={vision.conditionTitle || 'Clinical guidance'}
                  panelId="test-results-legacy-guidance-panel"
                  buttonId="test-results-legacy-guidance-trigger"
                  isOpen={guidanceAccordionIsOpen('legacy-guidance')}
                  onToggle={() => toggleGuidanceAccordion('legacy-guidance')}
                >
                  <div className="flex flex-col gap-4">
                    {vision.conditionSubtitle ? (
                      <p className="text-sm text-text-body dark:text-gray-400">{vision.conditionSubtitle}</p>
                    ) : null}
                    {vision.description ? (
                      <div>
                        <h4 className="text-sm font-semibold text-text-heading dark:text-white">Description</h4>
                        <p className="mt-1 text-sm text-text-body dark:text-gray-400">
                          <InlineHtml html={vision.description} />
                        </p>
                      </div>
                    ) : null}
                    {vision.symptomItems.length > 0 ? (
                      <div>
                        <h4 className="text-sm font-semibold text-text-heading dark:text-white">
                          Key signs, symptoms and lab abnormalities
                        </h4>
                        <dl className="mt-2 grid gap-3 sm:grid-cols-2">
                          {vision.symptomItems.map((s, idx) => (
                            <div
                              key={`${s.label}-${s.value}-${idx}`}
                              className="rounded-base border border-border-base dark:border-gray-700 p-3"
                            >
                              {s.label ? (
                                <dt className="text-xs font-medium text-text-body dark:text-gray-400">{s.label}</dt>
                              ) : null}
                              <dd
                                className={
                                  s.label
                                    ? 'mt-1 text-sm text-text-heading dark:text-white'
                                    : 'text-sm text-text-heading dark:text-white'
                                }
                              >
                                <InlineHtml html={s.value || '—'} />
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    ) : null}
                    {vision.clinicalBullets.length > 0 ? (
                      <div>
                        <h4 className="text-sm font-semibold text-text-heading dark:text-white">
                          Clinical considerations
                        </h4>
                        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-text-body dark:text-gray-400">
                          {vision.clinicalBullets.map((b) => (
                            <li key={b}>
                              <InlineHtml html={b} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </GuidanceAccordionPanel>
              ) : null}
            </div>
          ) : null}

          <GuidanceDisclaimer idSuffix="guidance" />
        </div>
      ) : (
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-text-body dark:text-gray-400 shadow-sm">
          Structured report summary and clinical guidance are not available for this report version.
        </div>
      )}
    </div>
  )
}

export default TestResultsCard
